import { getSum } from '@/lib/utils'
import dbscan from '@components/storeMap/categoryGrouping/dbscan'

import type { ShelfCategory } from '@components/storeMap/categoryGrouping/util'
import type { TransformedShelf } from '@state/shelf/types'

const categoryColors: Record<string, string> = {}

// Fixed set of distinguishable colors
const colorPalette = [
  'rgb(150, 190, 210)', // Darker Light Blue
  'rgb(240, 240, 160)', // Darker Light Yellow
  'rgb(150, 210, 210)', // Darker Pale Turquoise
  'rgb(200, 140, 200)', // Darker Plum
  'rgb(240, 190, 150)', // Darker Peach
  'rgb(150, 170, 200)', // Darker Light Steel Blue
  'rgb(240, 170, 180)', // Darker Pink
  'rgb(130, 220, 130)', // Darker Pale Green
  'rgb(160, 140, 220)', // Soft Purple
  'rgb(180, 220, 140)', // Light Olive Green
]

export function stringToColor(input: string): string {
  if (categoryColors[input]) return categoryColors[input]

  // Get the next color from the palette based on how many categories we've already assigned
  const colorIndex = Object.keys(categoryColors).length % colorPalette.length
  const color = colorPalette[colorIndex]!

  categoryColors[input] = color

  return color
}

export interface ShelfGroup {
  id: string
  category: ShelfCategory
  shelves: TransformedShelf[]
  center: { x: number; y: number }
}

export function groupShelvesByCategory(shelves: TransformedShelf[]): ShelfGroup[] {
  const categoryGroups = new Map<string, TransformedShelf[]>()

  shelves.forEach(shelf => {
    if (!shelf.statistics?.shelfCategory) return

    const category = shelf.statistics.shelfCategory
    if (!categoryGroups.has(category)) {
      categoryGroups.set(category, [])
    }

    categoryGroups.get(category)!.push(shelf)
  })

  return Array.from(categoryGroups.entries()).flatMap(([category, shelves]) => {
    const clusters = dbscan(shelves, { maxDistance: 4, minPoints: 3 })

    return clusters.map((shelves, i) => {
      const center = calculateCenter(shelves)
      return {
        id: `${category}-${i}`,
        category: category as ShelfCategory,
        shelves,
        center,
      }
    })
  })
}

function calculateCenter(shelves: TransformedShelf[]): { x: number; y: number } {
  if (shelves.length === 0) {
    return { x: 0, y: 0 }
  }

  const sumX = getSum(shelves, shelf => shelf.center.x)
  const sumY = getSum(shelves, shelf => shelf.center.y)

  return {
    x: sumX / shelves.length,
    y: sumY / shelves.length,
  }
}